/**
=========================================================
* E4A-IoT-Manager React Frontend Dashboard 
* Copyright © 2024 Electronics4All Inc.
=========================================================
*/

// @mui material components
import Card from "@mui/material/Card";
import DeleteTwoToneIcon from "@mui/icons-material/DeleteTwoTone";
import EditTwoToneIcon from "@mui/icons-material/EditTwoTone";
import VisibilityTwoToneIcon from "@mui/icons-material/VisibilityTwoTone";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import EqualizerTwoToneIcon from '@mui/icons-material/EqualizerTwoTone';
import Chip from "@mui/material/Chip";

// Material Dashboard React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// E4A React components
import DashboardLayout from "e4acomponents/LayoutContainers/DashboardLayout";
import DashboardNavbar from "e4acomponents/Navbars/DashboardNavbar";
import Footer from "e4acomponents/Footer";
import DataTable from "e4acomponents/Tables/DataTable";

// Inventory Management Components
import FiltersDialog from "./components/filters-dialog";
import AjustStockDialog from "./components/adjust-stock-dialog";
import { formatLocation, formatIdentifier } from "./components/helperFunctions";

// Permissions
import { AbilityContext } from "Can";
import { useAbility } from "@casl/react";
import { AuthContext } from "context";

// Data
import InventoryService from "services/inventory-items-service";
import LocationsService from "services/locations-service";
import { useEffect, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";

function InventoryView() {
    const ability = useAbility(AbilityContext);
    const [isLoading, setIsLoading] = useState(false); // Loading state for data fetching
    const [inventoryItems, setInventoryItems] = useState([]); // Inventory items fetched from the API
    const [tableData, setTableData] = useState([]); // Data to be displayed in the table
    const [showFiltersDialog, setShowFiltersDialog] = useState(false); // Visibility of the filters dialog
    const [showAdjustDialog, setShowAdjustDialog] = useState(false); // Visibility of the adjust stock level dialog
    const [locationsList, setLocationsList] = useState([]); // List of locations
    const [itemToAdjust, setItemToAdjust] = useState({
        id: "",
        initialQty: ""
    }); // Item to adjust in adjust stock level dialog

    const navigate = useNavigate();
    const authContext = useContext(AuthContext);
    // Fetches the inventory items from the API
    useEffect(() => {
        (async () => {
            setIsLoading(true);
            const items = await InventoryService.getAllInventoryItems();
            const locations = await LocationsService.getAllLocations();
            const indexedLocations = indexLocationsById(locations.data.attributes);
            setInventoryItems(items.data.attributes);
            setLocationsList(indexedLocations);
        })();
    }, []);

    // Sets up the table rows' data and refreshes the table when the inventoryItems changes
    useEffect(() => {
        setTableData(getRows(inventoryItems));
    }, [inventoryItems]);

    // Formats the fetched data to be displayed in the table
    const getRows = (info) => {
        let updatedInfo = info
            .map((e) => {
                const itemStatus = getStatus(e);
                const locationDetails = e.locationId ? locationsList[e.locationId] : null;
                return {
                    Partcode: formatIdentifier(e),
                    City: locationDetails ? locationDetails.city : e.city,
                    Location: formatLocation(e, locationDetails),
                    qty_in: e.quantityIn,
                    description: formatDescription(e),
                    manufpartnum: e.partCode.mpn ? 
                    e.partCode.mpn[e.partCode.preferred_mpn] : e.partCode.manufacturer_part_number,
                    id: e._id,
                    status: itemStatus[0],
                    statusChip:
                        <Tooltip title={itemStatus[2]}>
                            <Chip
                                label={itemStatus[0]}
                                color={itemStatus[1]}
                                variant="outlined"
                            />
                        </Tooltip>

                }
            })
        return updatedInfo;
    }

    const getStatus = (item) => {
        if (item.partCode.status.toLowerCase() === "pending approval" || item.partCode.status.toLowerCase() === "rejected") {
            return ["Inactive", "warning", "This item is inactive because its part code isn't approved yet."];
        } else if (item.partCode.status.toLowerCase() === "archived") {
            return ["Disabled", "secondary", "This item is disabled because its part code is archived."];
        } else if (item.partCode.status.toLowerCase() === "disabled") {
            return ["Disabled", "secondary", "This item is disabled because its part category is archived."];
        } else if (item.partCode.status.toLowerCase() === "obsolete") {
            return ["Obsolete", "secondary", "This item is obsolete because its part code is obsolete."];
        } else {
            return ["Active", "success", "This item is active."];
        }
    }

    // Formats the description to include the category code if its not already present (Legacy support)
    const formatDescription = (item) => {
        const categoryCode = item.identifier.split("-")[1];
        const description = item.partCode.description;
        const firstWord = description.split(' ')[0];
        if (firstWord !== categoryCode) {
            return `${categoryCode} ${description}`;
        }
        return description;
    }

    // Navigates to the view specific item page
    const goToViewPage = (id) => {
        navigate(`/inventory-management/view-specific-item/${id}`);
    }

    const handleOpenAdjustDialog = (qty, id) => {
        setItemToAdjust({
            id: id,
            initialQty: qty 
        });
        setShowAdjustDialog(true);
    }

    const handleCloseAdjustDialog = async () => {
        setShowAdjustDialog(false);

        // Refresh dataTable after adjusting stock
        const response = await InventoryService.getAllInventoryItems();
        setInventoryItems(response.data.attributes);
    }

    // Handles deleting inventory items
    const deleteItem = async (id) => {
        try {
            if (!confirm("Are you sure you want to delete this part?")) {
                e.nativeEvent.stopImmediatePropagation();
            } else {
                const userId = await authContext.getCurrentUser();
                await InventoryService.deleteInventoryItem(id, userId);

                const response = await InventoryService.getAllInventoryItems();
                setInventoryItems(response.data.attributes);
            }
        } catch (error) {
            console.log(error);
        }
    }

    // Headers and rows for the dataTable
    const dataTableData = {
        columns: [
            {
                Header: "Part Code",
                accessor: "Partcode",
                width: "15%",
                Cell: (info) => (
                    <MDTypography
                        component="a"
                        href="#"
                        variant="button"
                        fontWeight="medium"
                        onClick={() => goToViewPage(info.row.original.id)}
                        sx={{ cursor: "pointer", textDecoration: "none", color: "#344767" }}
                    >
                        {info.value}
                    </MDTypography>
                ),
            },
            {
                Header: "Warehouse",
                width: "11%",
                accessor: "City",
                type: "singleSelect",
                valueOptions: ["Kanata", "Smith Falls"],
            },
            {
                Header: "Location",
                width: "10%",
                accessor: "Location",
            },
            {
                Header: "Manuf Part Num",
                width: "10%",
                accessor: "manufpartnum",
            },
            {
                Header: "Description",
                width: "19%",
                accessor: "description",
            },
            {
                Header: "Quantity",
                width: "3%",
                accessor: "qty_in",
                type: "number",
            },
            {
                Header: "Status",
                width: "10%",
                accessor: "status",
                Cell: ({ row }) => row.original.statusChip,
                type: "singleSelect",
                valueOptions: ["Active", "Inactive", "Disabled", "Obsolete"],
            },
            {
                Header: "Actions",
                width: "17%",
                accessor: "actions",
                Cell: ({ row }) => {
                    const status = row.original.status;
                    const canAdjust = status === "Active" || status === "Obsolete";
                    const canDelete = status === "Active";
                    const canEdit = status === "Active";

                    return (
                        <MDBox display="flex" justifyContent="center">
                            {ability.can("Adjust", "InventoryItem") && (
                                <Tooltip title="Adjust Stock">
                                    <span>
                                        <IconButton
                                            disabled={!canAdjust}
                                            onClick={() =>
                                                canAdjust &&
                                                handleOpenAdjustDialog(row.original.qty_in, row.original.id)
                                            }
                                        >
                                            <EqualizerTwoToneIcon
                                                color={canAdjust ? "primary" : "disabled"}
                                            />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            )}

                            {ability.can("Delete", "InventoryItem") && (
                                <Tooltip title="Delete">
                                    <span>
                                        <IconButton
                                            disabled={!canDelete}
                                            onClick={() => canDelete && deleteItem(row.original.id)}
                                        >
                                            <DeleteTwoToneIcon
                                                color={canDelete ? "error" : "disabled"}
                                            />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            )}

                            {ability.can("access", "edit-inventory-item") && (
                                <Tooltip title="Edit">
                                    <span>
                                        <IconButton
                                            disabled={!canEdit}
                                            onClick={() =>
                                                canEdit &&
                                                navigate(
                                                    `/inventory-management/edit-inventory-item/${row.original.id}`
                                                )
                                            }
                                        >
                                            <EditTwoToneIcon
                                                color={canEdit ? "success" : "disabled"}
                                            />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            )}

                            <Tooltip title="View">
                                <span>
                                    <IconButton
                                        onClick={() =>
                                            navigate(
                                                `/inventory-management/view-specific-item/${row.original.id}`
                                            )
                                        }
                                    >
                                        <VisibilityTwoToneIcon color="info" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </MDBox>
                    );
                },
            },
        ],
        rows: tableData,
    };

    useEffect(() => {
        if (isLoading && tableData.length > 0) {
            setIsLoading(false);
        }
    }, [tableData]);

    // Opens the filters dialog
    const openFiltersDialog = () => {
        setShowFiltersDialog(true);
    }

    // Closes the filters dialog
    const closeFiltersDialog = () => {
        setShowFiltersDialog(false);
    }

    // Applies the selected filters to the dataTable
    const applyFilters = (LocationFilters) => {
        setShowFiltersDialog(false);
        setTableData(
            getRows(
                inventoryItems.
                    filter((item) => item.locationId ?
                        LocationFilters.includes(locationsList[item.locationId].city) :
                        LocationFilters.includes(item.city)
                    )
            ));
    }

    // Indexes locations by their ID for easier access
    const indexLocationsById = (locations) => {
        if (!Array.isArray(locations)) return {};

        const indexedById = {};
        for (const doc of locations) {
            if (doc && doc._id != null) {
                indexedById[doc._id] = doc;
            }
        }
        return indexedById;
    }

    return (
        <DashboardLayout>
            <DashboardNavbar />

            <FiltersDialog
                handleClose={closeFiltersDialog}
                open={showFiltersDialog}
                submit={applyFilters}
            />

            <AjustStockDialog
                handleClose={handleCloseAdjustDialog}
                open={showAdjustDialog}
                id={itemToAdjust.id}
                initial_qty={itemToAdjust.initialQty}
            />

            <MDBox pt={6} pb={3}>

                <Card>

                    <MDBox p={3} lineHeight={1} display="flex" justifyContent="space-between">

                        {/* Title */}
                        <MDTypography variant="h5" fontWeight="medium">
                            Inventory Items List
                        </MDTypography>

                        {/* Add New Item Button */}
                        {ability.can("access", "create-inventory-item") && (
                            <MDButton
                                variant="gradient"
                                color="info"
                                size="small"
                                type="submit"
                                onClick={() => { navigate("/inventory-management/create-inventory-item") }}
                            >
                                + Add New Item
                            </MDButton>
                        )}
                    </MDBox>

                    {/* DataTable */}
                    <MDBox>
                        <DataTable
                            table={dataTableData}
                            canSearch
                            hasFilters
                            openFiltersModal={openFiltersDialog}
                            isLoading={isLoading}
                            showToolbar
                            showEntriesPerPage
                            tableId="inventory-items-table"
                        />
                    </MDBox>

                </Card>

            </MDBox>

            <MDBox>
                <Footer />
            </MDBox>

        </DashboardLayout>
    )
}

export default InventoryView;